/**
 * The bake's PIXEL chain, as one pure function.
 *
 * WS-J J-17 / D-9. AUD-2 IM-6 measured a 2048² upload-bake at 1.5–2.5 s on the main thread and a
 * live compose — the 320 ms debounce behind every slider notch — at 0.8–1.2 s. Most of that is
 * this chain: solidify → AI-edge refine → feather → shell-alpha → coverage counts. It ran inside
 * `composeMockupOnce`, closed over a component, and therefore could only ever run on the thread
 * that also has to paint.
 *
 * Splitting it out is what makes it movable. There is exactly ONE implementation: the worker calls
 * this, and so does the main-thread fallback (`composeWorkerClient.ts`), so "worker" and "no
 * worker" cannot diverge — a claim `compose-worker-client.test.ts` checks byte for byte rather
 * than assumes.
 *
 * No DOM, no canvas, no React. The order of operations is the order the bake has always run them
 * in; `compose-pipeline.test.ts` compares the result against `solidifyPrintWindow` called directly,
 * because a re-ordered chain is a different cutout.
 */
import { solidifyPrintWindow } from "./printWindow.js";
import { productMaskFromAlpha } from "./compositing.js";

export interface ComposePixelInput {
  width: number;
  height: number;
  /** The print-area coverage plane (WS-I's `chromaMarkerFromPristine().printAlpha`). */
  printAlpha: Uint8Array;
  /** The source RGBA, for the product mask. */
  sourceRgba: Uint8ClampedArray;
  /** The despilled product RGBA, for the shell. */
  despilled: Uint8ClampedArray;
  /** The RAW matte plane, or null. A COPY — the caller retains the original. */
  matte: Uint8Array | null;
  feather: number;
  aiEdge: { keep: number } | null;
  /**
   * A print guide key: the picture follows the key itself (owner, 2026-10-14: the controls
   * "changing the existing one, not the original image"). Its coverage is used as it is, not
   * doubled, and the colours over the guide are the key's own, so the case's see-through lines
   * and shadows stay as the key left them.
   */
  keyedShell?: boolean;
}

export interface ComposePixelResult {
  printSolid: Uint8Array;
  shellRgba: Uint8ClampedArray;
  productMask: Uint8Array;
  usedAiEdge: boolean;
  aiEdgeSkipped: boolean;
  printCoveragePct: number;
  matteCoveragePct: number;
}

/** The bake's pixel chain, in the order the bake has always run it. Pure; no DOM, no canvas. */
export function composePixels(input: ComposePixelInput): ComposePixelResult {
  const { width, height, printAlpha, sourceRgba, despilled, matte, feather, aiEdge, keyedShell } = input;
  const n = width * height;

  // The product mask comes from the CURRENT source alpha — i.e. the AI auto-clean mask the user
  // already applied (no classical re-segmentation, spec 022).
  const productMask = productMaskFromAlpha(sourceRgba, width, height);

  // The solidify → AI edge → feather chain lives in `printWindow.ts` (WS-I Task I-4) so the
  // bake-parity test can run THIS code instead of a copy of it.
  const window = solidifyPrintWindow(printAlpha, width, height, {
    gain: keyedShell ? 1 : undefined,
    feather,
    aiEdge: aiEdge && matte ? { matte, keep: aiEdge.keep } : null,
  });
  const printSolid = window.mask;

  // F-38 (J-16): ONE pass over N. The shell-alpha write, the print-coverage count and (when the
  // matte is the same plane size) the matte-coverage count all walk the same index.
  const shellRgba = new Uint8ClampedArray(despilled);
  const matteFused = !!matte && matte.length === n;
  let pc = 0;
  let mc = 0;
  for (let i = 0; i < n; i++) {
    if (keyedShell && printAlpha[i]) shellRgba.set(sourceRgba.subarray(i * 4, i * 4 + 3), i * 4);
    shellRgba[i * 4 + 3] = (productMask[i] * (255 - printSolid[i])) / 255;
    if (printSolid[i] >= 128) pc++;
    if (matteFused && matte![i] >= 128) mc++;
  }
  let matteCoveragePct = 0;
  if (matte) {
    if (!matteFused) {
      mc = 0;
      for (let i = 0; i < matte.length; i++) if (matte[i] >= 128) mc++;
    }
    matteCoveragePct = (100 * mc) / matte.length;
  }

  return {
    printSolid,
    shellRgba,
    productMask,
    usedAiEdge: window.usedAiEdge,
    aiEdgeSkipped: window.aiEdgeSkipped,
    printCoveragePct: (100 * pc) / printSolid.length,
    matteCoveragePct,
  };
}
